import {useEffect, useMemo, useState} from "react";
import {Check, Info, Pencil, RotateCcw} from "lucide-react";
import {useAuth} from "@/context/use-auth";
import {useAdminTranslation, useCommonTranslation} from "@/i18n/hooks";
import {useToast} from "@/hooks/use-toast";
import {Badge} from "@/components/tailwind/badge";
import {Button} from "@/components/ui/button";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {Field, Label} from "@/components/tailwind/fieldset";
import {Input} from "@/components/tailwind/input";
import {Switch, SwitchField} from "@/components/tailwind/switch";
import {Text} from "@/components/tailwind/text";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip";
import PasswordStrengthIndicator from "@/components/password-strength-indicator";
import {
    AdminPasswordPolicyResponse,
    EffectivePasswordPolicySource,
    getAdminPasswordPolicy,
    PasswordPolicyUpdateRequest,
    resetAdminPasswordPolicy,
    updateAdminPasswordPolicy,
} from "@/services/password-policy-services";
import log from "@/lib/logger";

// Hard minimum mirrored from NbhPasswordPolicyConfiguration.MIN_LENGTH_FLOOR. The server stays the
// source of truth; this is purely UX so the input cannot go below the floor.
const MIN_LENGTH_FLOOR = 8;
const MAX_LENGTH_CEILING = 128;

// Upper bound for zxcvbn's basic score. Mirrors NbhPasswordPolicyConfiguration.MAX_STRENGTH_SCORE.
// 0 means "no minimum strength" (the constraint is inactive); 1..4 require the password to score at
// least that level when nbvcxz evaluates it on the server.
const MAX_STRENGTH_SCORE = 4;
type StrengthScore = 0 | 1 | 2 | 3 | 4;

// Stable machine-readable code attached to the 409 ProblemDetail by
// PasswordPolicyAdminControllerAdvice when per-organization overrides are disabled installation
// wide. Distinct from the other 409 outcome (concurrent modification), so the section must match
// on this code, not on the bare 409 status.
const PER_ORG_DISABLED_CODE = "PASSWORD_POLICY_PER_ORG_DISABLED";

function isPerOrgDisabledError(error: unknown): boolean {
    const detail = error as {code?: string; status?: number} | null;
    return detail?.status === 409 && detail.code === PER_ORG_DISABLED_CODE;
}

interface PolicyFormState {
    minLength: number;
    requireDigit: boolean;
    requireSpecialChar: boolean;
    blockUsername: boolean;
    blockRealName: boolean;
    blockOrganizationName: boolean;
    hibpEnabled: boolean;
    minDistinctChars: number;
    minStrengthScore: StrengthScore;
}

export function clampStrengthScore(value: number): StrengthScore {
    // Guard against NaN/undefined coming in from a partial or rolling-deploy response: Math.round(NaN)
    // would propagate into form state and serialize as null in the PUT payload, breaking the select.
    if (!Number.isFinite(value) || value <= 0) return 0;
    if (value >= MAX_STRENGTH_SCORE) return MAX_STRENGTH_SCORE;
    return Math.round(value) as StrengthScore;
}

function policyToFormState(policy: AdminPasswordPolicyResponse): PolicyFormState {
    return {
        minLength: policy.minLength,
        requireDigit: policy.requireDigit,
        requireSpecialChar: policy.requireSpecialChar,
        blockUsername: policy.blockUsername,
        blockRealName: policy.blockRealName,
        blockOrganizationName: policy.blockOrganizationName,
        hibpEnabled: policy.hibpEnabled,
        minDistinctChars: policy.minDistinctChars,
        minStrengthScore: clampStrengthScore(policy.minStrengthScore),
    };
}

function formStateToRequest(form: PolicyFormState): PasswordPolicyUpdateRequest {
    return {
        minLength: form.minLength,
        requireDigit: form.requireDigit,
        requireSpecialChar: form.requireSpecialChar,
        blockUsername: form.blockUsername,
        blockRealName: form.blockRealName,
        blockOrganizationName: form.blockOrganizationName,
        hibpEnabled: form.hibpEnabled,
        minDistinctChars: form.minDistinctChars,
        minStrengthScore: form.minStrengthScore,
    };
}

// Build the weakest password that still satisfies the current form values. zxcvbn-ts then estimates
// how long it would take to crack so the admin can judge whether the policy is strong enough. The
// baseline alternates two uncommon lowercase letters so the synthetic candidate is not itself in
// the bundled common-password blocklist (e.g. "aaaaaaaa" is on the Top-10000 list and would be
// rejected at validation time, which would make the indicator misleading). zxcvbn still rates the
// "pqpq…" pattern as low-entropy, so the meter remains a useful "weakest allowed" baseline.
const BASELINE_CHARS = ["p", "q"] as const;

// 68-character fallback used when the policy requires more distinct characters than the 2-char
// baseline can provide. Covers all 26 lowercase letters, 10 digits, 26 uppercase letters and
// 6 common specials — large enough to satisfy any realistic minDistinctChars within the
// MAX_LENGTH_CEILING (#679). Order chosen to avoid spelling common words or stacking obvious
// sequences when prefix-stacked, so the synthetic candidate stays out of the bundled Top-10000
// blocklist.
const DISTINCT_BASELINE_CHARS = [
    "p", "q", "x", "z", "v", "w", "y", "k", "j", "f",
    "h", "g", "m", "n", "b", "l", "c", "d", "r", "t",
    "s", "u", "i", "o", "a", "e",
    "1", "2", "3", "4", "5", "6", "7", "8", "9", "0",
    "P", "Q", "X", "Z", "V", "W", "Y", "K", "J", "F",
    "H", "G", "M", "N", "B", "L", "C", "D", "R", "T",
    "S", "U", "I", "O", "A", "E",
    "!", "?", "#", "*", "@", "+",
] as const;

export function buildWeakestSyntheticPassword(form: PolicyFormState): string {
    // Clamp to both the floor and the UI ceiling: the backend imposes no upper bound on minLength
    // (or minDistinctChars), so an unexpectedly large value would otherwise allocate a huge string
    // and feed it into zxcvbn, potentially freezing the settings page while it runs the estimator.
    // The length must additionally cover minDistinctChars: every distinct character requires its
    // own slot, so a shorter password could never satisfy the constraint.
    const minDistinctChars = Math.max(0, form.minDistinctChars);
    const length = Math.min(
        MAX_LENGTH_CEILING,
        Math.max(MIN_LENGTH_FLOOR, form.minLength, minDistinctChars),
    );
    // Cap distinctTarget at the fallback alphabet size so the helper never cycles. Cycling would
    // produce a password whose distinct count is below the requested minDistinctChars, breaking
    // the "weakest allowed password" claim and misleading the strength indicator. For policies
    // beyond the alphabet size we cap at the alphabet and let the indicator drive zxcvbn off the
    // longest password the alphabet supports; those policies are well outside realistic use.
    const distinctTarget = Math.min(length, minDistinctChars, DISTINCT_BASELINE_CHARS.length);

    const chars: string[] = new Array(length);
    // Fill the leading distinctTarget slots with distinct characters from the fallback alphabet;
    // the remainder reuses the original two-char baseline so the rest stays low-entropy.
    for (let i = 0; i < distinctTarget; i += 1) {
        chars[i] = DISTINCT_BASELINE_CHARS[i] as string;
    }
    for (let i = distinctTarget; i < length; i += 1) {
        chars[i] = BASELINE_CHARS[i % BASELINE_CHARS.length] as string;
    }

    let nextSlot = chars.length - 1;
    if (form.requireDigit && nextSlot >= 0) {
        chars[nextSlot] = "1";
        nextSlot -= 1;
    }
    if (form.requireSpecialChar && nextSlot >= 0) {
        chars[nextSlot] = "!";
    }
    return chars.join("");
}

function badgeColorForSource(source: EffectivePasswordPolicySource): "blue" | "emerald" {
    return source === "ORGANIZATION_OVERRIDE" ? "emerald" : "blue";
}

export default function SectionPasswordPolicy() {
    const {token} = useAuth();
    const {t} = useAdminTranslation();
    const {toast} = useToast();

    const [policy, setPolicy] = useState<AdminPasswordPolicyResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [form, setForm] = useState<PolicyFormState | null>(null);

    useEffect(() => {
        if (!token) {
            // Without a token the GET cannot run; clear the loading spinner instead of leaving the
            // section in a perpetual "Lädt..." state. The admin-settings-page guard already
            // redirects unauthenticated/non-admin users away, so this branch is mostly defensive
            // (e.g. transient state during logout or token expiry).
            setIsLoading(false);
            setPolicy(null);
            return;
        }
        let cancelled = false;
        setIsLoading(true);
        getAdminPasswordPolicy(token)
            .then((data) => {
                if (cancelled || !data) return;
                setPolicy(data);
            })
            .catch((error) => {
                log.error("Error fetching admin password policy:", error);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [token]);

    function startEditing() {
        if (!policy) return;
        setForm(policyToFormState(policy));
        setIsEditing(true);
    }

    function cancelEditing() {
        setForm(null);
        setIsEditing(false);
    }

    async function save() {
        if (!form) return;
        setIsSaving(true);
        try {
            const updated = await updateAdminPasswordPolicy(token, formStateToRequest(form));
            if (updated) {
                setPolicy(updated);
            }
            setIsEditing(false);
            setForm(null);
            toast({
                title: t("adminSettings.passwordPolicy.toast.saveSuccess.title"),
                description: t("adminSettings.passwordPolicy.toast.saveSuccess.description"),
            });
        } catch (error: unknown) {
            log.error("Error saving admin password policy:", error);
            if (isPerOrgDisabledError(error)) {
                toast({
                    title: t("adminSettings.passwordPolicy.toast.perOrgDisabled.title"),
                    description: t("adminSettings.passwordPolicy.toast.perOrgDisabled.description"),
                    variant: "destructive",
                });
            } else {
                toast({
                    title: t("adminSettings.passwordPolicy.toast.saveError.title"),
                    description: t("adminSettings.passwordPolicy.toast.saveError.description"),
                    variant: "destructive",
                });
            }
        } finally {
            setIsSaving(false);
        }
    }

    async function resetToDefault() {
        setIsResetting(true);
        try {
            const updated = await resetAdminPasswordPolicy(token);
            if (updated) {
                setPolicy(updated);
            }
            setIsEditing(false);
            setForm(null);
            toast({
                title: t("adminSettings.passwordPolicy.toast.resetSuccess.title"),
                description: t("adminSettings.passwordPolicy.toast.resetSuccess.description"),
            });
        } catch (error: unknown) {
            log.error("Error resetting admin password policy:", error);
            if (isPerOrgDisabledError(error)) {
                toast({
                    title: t("adminSettings.passwordPolicy.toast.perOrgDisabled.title"),
                    description: t("adminSettings.passwordPolicy.toast.perOrgDisabled.description"),
                    variant: "destructive",
                });
            } else {
                toast({
                    title: t("adminSettings.passwordPolicy.toast.resetError.title"),
                    description: t("adminSettings.passwordPolicy.toast.resetError.description"),
                    variant: "destructive",
                });
            }
        } finally {
            setIsResetting(false);
        }
    }

    const indicatorForm = useMemo<PolicyFormState | null>(() => {
        if (isEditing && form) return form;
        if (policy) return policyToFormState(policy);
        return null;
    }, [isEditing, form, policy]);

    const syntheticPassword = useMemo(() => {
        if (!indicatorForm) return "";
        return buildWeakestSyntheticPassword(indicatorForm);
    }, [indicatorForm]);

    const readOnly = policy ? !policy.perOrganizationEnabled : false;
    const showOverrideReset = !readOnly && policy?.source === "ORGANIZATION_OVERRIDE";

    return (
        <Card className="p-6 mt-6 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
            <CardHeader>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 flex-wrap">
                        <CardTitle className="text-xl font-bold">
                            {t("adminSettings.passwordPolicy.title")}
                        </CardTitle>
                        {policy && (
                            <Badge
                                color={badgeColorForSource(policy.source)}
                                data-testid="password-policy-source-badge"
                                data-source={policy.source}
                            >
                                {t(`adminSettings.passwordPolicy.source.${policy.source}`)}
                            </Badge>
                        )}
                    </div>

                    {!isEditing && !readOnly && policy && (
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={startEditing}
                                className="flex items-center"
                                aria-label={t("adminSettings.passwordPolicy.button.edit.ariaLabel")}
                                data-testid="edit-password-policy-button"
                            >
                                <Pencil className="h-4 w-4 mr-1"/>
                                {t("adminSettings.passwordPolicy.button.edit.text")}
                            </Button>
                            {showOverrideReset && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={resetToDefault}
                                    disabled={isResetting}
                                    className="flex items-center"
                                    data-testid="reset-password-policy-button"
                                >
                                    <RotateCcw className="h-4 w-4 mr-1"/>
                                    {isResetting
                                        ? t("adminSettings.passwordPolicy.button.reset.resetting")
                                        : t("adminSettings.passwordPolicy.button.reset.text")}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
                <Text className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {t("adminSettings.passwordPolicy.description")}
                </Text>
            </CardHeader>

            <CardContent>
                {isLoading ? (
                    <div
                        className="text-sm text-gray-500 dark:text-gray-400 py-2"
                        data-testid="password-policy-loading"
                    >
                        {t("adminSettings.passwordPolicy.loading")}
                    </div>
                ) : policy ? (
                    <div className="space-y-6">
                        {readOnly && (
                            <div
                                className="rounded-md border-l-4 border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-950 p-3 text-sm text-blue-800 dark:text-blue-200"
                                data-testid="password-policy-readonly-banner"
                            >
                                {t("adminSettings.passwordPolicy.perOrganizationDisabled")}
                            </div>
                        )}

                        {isEditing && form ? (
                            <EditForm
                                form={form}
                                onChange={setForm}
                                isSaving={isSaving}
                                onSave={save}
                                onCancel={cancelEditing}
                            />
                        ) : (
                            <DisplayValues policy={policy}/>
                        )}

                        {syntheticPassword && (
                            <div className="mt-2" data-testid="password-policy-indicator">
                                <Text className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {t("adminSettings.passwordPolicy.indicator.title")}
                                </Text>
                                <Text className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {t("adminSettings.passwordPolicy.indicator.description")}
                                </Text>
                                <PasswordStrengthIndicator
                                    password={syntheticPassword}
                                    minScore={indicatorForm?.minStrengthScore ?? 0}
                                />
                            </div>
                        )}
                    </div>
                ) : null}
            </CardContent>
        </Card>
    );
}

type DisplayItem =
    | {kind: "value"; key: string; label: string; value: string; testid: string}
    | {kind: "active"; key: string; label: string; on: boolean; testid: string; tooltip?: TooltipMeta}
    | {kind: "block"; key: string; label: string; on: boolean; testid: string};

interface TooltipMeta {
    content: string;
    ariaLabel: string;
    testid: string;
}

// Inline info icon that reveals a short hover/focus tooltip — used to keep field labels free of
// explanatory copy that would otherwise clutter the display mode. The fuller documentation lives
// in the user-facing handbook (#647).
function InfoTooltip({content, ariaLabel, testid}: TooltipMeta) {
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        aria-label={ariaLabel}
                        className="inline-flex items-center text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                        data-testid={testid}
                    >
                        <Info className="h-3.5 w-3.5"/>
                    </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">{content}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

function DisplayValues({policy}: {policy: AdminPasswordPolicyResponse}) {
    const {t} = useAdminTranslation();
    const {t: tCommon} = useCommonTranslation();
    const items: DisplayItem[] = [
        {
            kind: "value",
            key: "minLength",
            label: t("adminSettings.passwordPolicy.displayLabels.minLength"),
            value: String(policy.minLength),
            testid: "display-min-length",
        },
        {
            kind: "value",
            key: "minDistinctChars",
            label: t("adminSettings.passwordPolicy.displayLabels.minDistinctChars"),
            value: String(policy.minDistinctChars),
            testid: "display-min-distinct-chars",
        },
        {
            kind: "value",
            key: "minStrengthScore",
            label: t("adminSettings.passwordPolicy.displayLabels.minStrengthScore"),
            value: t(`adminSettings.passwordPolicy.strengthScoreLabels.${policy.minStrengthScore}`),
            testid: "display-min-strength-score",
        },
        {
            kind: "active",
            key: "requireDigit",
            label: t("adminSettings.passwordPolicy.displayLabels.requireDigit"),
            on: policy.requireDigit,
            testid: "display-require-digit",
        },
        {
            kind: "active",
            key: "requireSpecialChar",
            label: t("adminSettings.passwordPolicy.displayLabels.requireSpecialChar"),
            on: policy.requireSpecialChar,
            testid: "display-require-special-char",
        },
        {
            kind: "block",
            key: "blockUsername",
            label: t("adminSettings.passwordPolicy.displayLabels.blockUsername"),
            on: policy.blockUsername,
            testid: "display-block-username",
        },
        {
            kind: "block",
            key: "blockRealName",
            label: t("adminSettings.passwordPolicy.displayLabels.blockRealName"),
            on: policy.blockRealName,
            testid: "display-block-real-name",
        },
        {
            kind: "block",
            key: "blockOrganizationName",
            label: t("adminSettings.passwordPolicy.displayLabels.blockOrganizationName"),
            on: policy.blockOrganizationName,
            testid: "display-block-organization-name",
        },
        {
            kind: "active",
            key: "bundledBlocklistEnabled",
            label: t("adminSettings.passwordPolicy.displayLabels.bundledBlocklistEnabled"),
            on: policy.bundledBlocklistEnabled,
            testid: "display-bundled-blocklist-enabled",
        },
        {
            kind: "active",
            key: "hibpEnabled",
            label: t("adminSettings.passwordPolicy.displayLabels.hibpEnabled"),
            on: policy.hibpEnabled,
            testid: "display-hibp-enabled",
            tooltip: {
                content: t("adminSettings.passwordPolicy.hibpTooltip"),
                ariaLabel: t("adminSettings.passwordPolicy.hibpTooltipAriaLabel"),
                testid: "display-hibp-info",
            },
        },
    ];

    return (
        <div className="grid gap-4 md:grid-cols-2">
            {items.map((item) => (
                <div key={item.key}>
                    <div className="flex items-center gap-1.5">
                        <Text className="text-xs text-gray-500 dark:text-gray-400">{item.label}</Text>
                        {item.kind === "active" && item.tooltip && (
                            <InfoTooltip {...item.tooltip}/>
                        )}
                    </div>
                    {item.kind === "value" && (
                        <Text className="text-sm font-medium" data-testid={item.testid}>
                            {item.value}
                        </Text>
                    )}
                    {item.kind === "active" && (
                        <div className="mt-1">
                            <Badge
                                color={item.on ? "emerald" : "zinc"}
                                data-testid={item.testid}
                                data-state={item.on ? "active" : "inactive"}
                            >
                                {item.on ? tCommon("fields.active") : tCommon("fields.inactive")}
                            </Badge>
                        </div>
                    )}
                    {item.kind === "block" && (
                        <div className="mt-1">
                            <Badge
                                color={item.on ? "amber" : "zinc"}
                                data-testid={item.testid}
                                data-state={item.on ? "blocked" : "allowed"}
                            >
                                {item.on
                                    ? t("adminSettings.passwordPolicy.blockState.blocked")
                                    : t("adminSettings.passwordPolicy.blockState.allowed")}
                            </Badge>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

interface EditFormProps {
    form: PolicyFormState;
    onChange: (next: PolicyFormState) => void;
    isSaving: boolean;
    onSave: () => void;
    onCancel: () => void;
}

function EditForm({form, onChange, isSaving, onSave, onCancel}: EditFormProps) {
    const {t} = useAdminTranslation();

    function update<K extends keyof PolicyFormState>(key: K, value: PolicyFormState[K]) {
        onChange({...form, [key]: value});
    }

    // The previous version clamped the lower bound on every onChange, which made multi-digit values
    // impossible to type ("14" became "84" because "1" was immediately raised to the floor). The
    // upper bound clamps fine on the fly; the lower bound is enforced at save time via the disabled
    // button + inline hint. The empty-input case stores 0 so the controlled input can render "".
    function handleMinLengthChange(raw: string) {
        if (raw === "") {
            update("minLength", 0);
            return;
        }
        const parsed = parseInt(raw, 10);
        if (Number.isNaN(parsed) || parsed < 0) return;
        const clamped = Math.min(MAX_LENGTH_CEILING, parsed);
        update("minLength", clamped);
    }

    // Same shape as handleMinLengthChange but for minDistinctChars (#679). 0 disables the
    // constraint; the upper bound matches MAX_LENGTH_CEILING because every distinct character needs
    // its own slot in the password.
    function handleMinDistinctCharsChange(raw: string) {
        if (raw === "") {
            update("minDistinctChars", 0);
            return;
        }
        const parsed = parseInt(raw, 10);
        if (Number.isNaN(parsed) || parsed < 0) return;
        const clamped = Math.min(MAX_LENGTH_CEILING, parsed);
        update("minDistinctChars", clamped);
    }

    const minLengthInvalid = form.minLength < MIN_LENGTH_FLOOR;
    const minDistinctCharsInvalid = form.minDistinctChars > MAX_LENGTH_CEILING;

    return (
        <div className="space-y-4">
            <Field>
                <Label>{t("adminSettings.passwordPolicy.fields.minLength")}</Label>
                <Input
                    type="number"
                    min={MIN_LENGTH_FLOOR}
                    max={MAX_LENGTH_CEILING}
                    value={form.minLength === 0 ? "" : form.minLength}
                    onChange={(e) => handleMinLengthChange(e.target.value)}
                    data-testid="password-policy-min-length-input"
                    aria-invalid={minLengthInvalid || undefined}
                />
                {minLengthInvalid ? (
                    <Text
                        className="text-xs text-red-600 dark:text-red-400 mt-1"
                        data-testid="password-policy-min-length-error"
                    >
                        {t("adminSettings.passwordPolicy.validation.minLengthBelowFloor", {floor: MIN_LENGTH_FLOOR})}
                    </Text>
                ) : (
                    <Text className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t("adminSettings.passwordPolicy.fields.minLengthHint")}
                    </Text>
                )}
            </Field>

            <Field>
                <Label>{t("adminSettings.passwordPolicy.fields.minDistinctChars")}</Label>
                <Input
                    type="number"
                    min={0}
                    max={MAX_LENGTH_CEILING}
                    value={form.minDistinctChars === 0 ? "" : form.minDistinctChars}
                    onChange={(e) => handleMinDistinctCharsChange(e.target.value)}
                    data-testid="password-policy-min-distinct-chars-input"
                    aria-invalid={minDistinctCharsInvalid || undefined}
                />
                {minDistinctCharsInvalid ? (
                    <Text
                        className="text-xs text-red-600 dark:text-red-400 mt-1"
                        data-testid="password-policy-min-distinct-chars-error"
                    >
                        {t("adminSettings.passwordPolicy.validation.minDistinctCharsAboveCeiling", {
                            ceiling: MAX_LENGTH_CEILING,
                        })}
                    </Text>
                ) : (
                    <Text className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t("adminSettings.passwordPolicy.fields.minDistinctCharsHint")}
                    </Text>
                )}
            </Field>

            <Field>
                <Label>{t("adminSettings.passwordPolicy.fields.minStrengthScore")}</Label>
                <select
                    className="block w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-1.5 text-sm text-zinc-900 dark:text-zinc-100"
                    value={form.minStrengthScore}
                    onChange={(e) => update("minStrengthScore", clampStrengthScore(parseInt(e.target.value, 10)))}
                    data-testid="password-policy-min-strength-score-select"
                >
                    {[0, 1, 2, 3, 4].map((score) => (
                        <option key={score} value={score}>
                            {t(`adminSettings.passwordPolicy.strengthScoreLabels.${score}`)}
                        </option>
                    ))}
                </select>
                <Text className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t("adminSettings.passwordPolicy.fields.minStrengthScoreHint")}
                </Text>
            </Field>

            <SwitchField>
                <Label>{t("adminSettings.passwordPolicy.fields.requireDigit")}</Label>
                <Switch
                    checked={form.requireDigit}
                    onChange={(value) => update("requireDigit", value)}
                    color="blue"
                    data-testid="password-policy-require-digit-switch"
                />
            </SwitchField>

            <SwitchField>
                <Label>{t("adminSettings.passwordPolicy.fields.requireSpecialChar")}</Label>
                <Switch
                    checked={form.requireSpecialChar}
                    onChange={(value) => update("requireSpecialChar", value)}
                    color="blue"
                    data-testid="password-policy-require-special-char-switch"
                />
            </SwitchField>

            <SwitchField>
                <Label>{t("adminSettings.passwordPolicy.fields.blockUsername")}</Label>
                <Switch
                    checked={form.blockUsername}
                    onChange={(value) => update("blockUsername", value)}
                    color="blue"
                    data-testid="password-policy-block-username-switch"
                />
            </SwitchField>

            <SwitchField>
                <Label>{t("adminSettings.passwordPolicy.fields.blockRealName")}</Label>
                <Switch
                    checked={form.blockRealName}
                    onChange={(value) => update("blockRealName", value)}
                    color="blue"
                    data-testid="password-policy-block-real-name-switch"
                />
            </SwitchField>

            <SwitchField>
                <Label>{t("adminSettings.passwordPolicy.fields.blockOrganizationName")}</Label>
                <Switch
                    checked={form.blockOrganizationName}
                    onChange={(value) => update("blockOrganizationName", value)}
                    color="blue"
                    data-testid="password-policy-block-organization-name-switch"
                />
            </SwitchField>

            <SwitchField>
                <Label>
                    <span className="inline-flex items-center gap-1.5">
                        {t("adminSettings.passwordPolicy.fields.hibpEnabled")}
                        <InfoTooltip
                            content={t("adminSettings.passwordPolicy.hibpTooltip")}
                            ariaLabel={t("adminSettings.passwordPolicy.hibpTooltipAriaLabel")}
                            testid="edit-hibp-info"
                        />
                    </span>
                </Label>
                <Switch
                    checked={form.hibpEnabled}
                    onChange={(value) => update("hibpEnabled", value)}
                    color="blue"
                    data-testid="password-policy-hibp-enabled-switch"
                />
            </SwitchField>

            <div className="flex space-x-2">
                <Button
                    size="sm"
                    onClick={onSave}
                    disabled={isSaving || minLengthInvalid || minDistinctCharsInvalid}
                    className="flex items-center"
                    data-testid="save-password-policy-button"
                >
                    <Check className="h-4 w-4 mr-1"/>
                    {isSaving
                        ? t("adminSettings.passwordPolicy.button.save.saving")
                        : t("adminSettings.passwordPolicy.button.save.save")}
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onCancel}
                    disabled={isSaving}
                    data-testid="cancel-password-policy-button"
                >
                    {t("adminSettings.passwordPolicy.button.cancel")}
                </Button>
            </div>
        </div>
    );
}
